// resources/public.jsx
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useNotify } from "react-admin";
import { Box, Container, Paper, Typography, TextField, Button, CircularProgress, Alert } from "@mui/material";

const apiEndpoint = process.env.REACT_APP_TURUK_CLINIC_ADMIN_REST_API;
const appointmentSignUpSuccessURL = process.env.REACT_APP_TURUK_APPOINTMENT_SUCCESS_URL;

const EmptyLayout = ({ children }) => (
  <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
    {children}
  </Box>
);

export const PublicAppointmentShow = () => {
  const { id } = useParams(); // Получаем ID записи из URL
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [extraQuestions, setExtraQuestions] = useState([]);
  const [formData, setFormData] = useState({
    answers: {}
  });

  // Загружаем данные о записи и дополнительные вопросы
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Получаем дополнительные вопросы клиники
        const questionsResponse = await fetch(`${apiEndpoint}/appointments/${id}/public/extra_appointment_questions`);
        const questionsData = await questionsResponse.json();
        setExtraQuestions(questionsData || []);
        
        // Инициализируем answers для вопросов
        const initialAnswers = {};
        (questionsData.questions || []).forEach((question) => {
          initialAnswers[question.text] = '';
        });
        setFormData(prev => ({ ...prev, answers: initialAnswers }));
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        notify('Ошибка при загрузке данных', { type: 'error' });
        setLoading(false);
      }
    };
    
    if (id) {
      fetchData();
    }
  }, [id, notify]);

  const handleAnswerChange = (questionText, answer) => {
    setFormData(prev => ({
      ...prev,
      answers: { ...prev.answers, [questionText]: answer }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await fetch(`${apiEndpoint}/appointments/${id}/public/extra_appointment_answers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointment_id: id,
          answers: formData.answers,
        })
      });
      
      if (response.ok) {
        notify('Запись успешно подтверждена!', { type: 'success' });
        // Перенаправляем на страницу успеха
        setTimeout(() => {
          window.location.href = appointmentSignUpSuccessURL;
        }, 2000);
      } else {
        const error = await response.json();
        notify(error.detail || 'Ошибка при сохранении', { type: 'error' });
      }
    } catch (error) {
      console.error('Error submitting:', error);
      notify('Ошибка при сохранении', { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <EmptyLayout>
        <Container maxWidth="md">
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Загрузка данных...</Typography>
          </Paper>
        </Container>
      </EmptyLayout>
    );
  }

  return (
    <EmptyLayout>
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4 }}>
          <form onSubmit={handleSubmit}>
            {extraQuestions.length > 0 && (
              <>
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Заполните данные для записи:
                </Typography>
                {extraQuestions.map((question, index) => (
                  <TextField
                    key={index}
                    fullWidth
                    label={question.text}
                    value={formData.answers[question.text] || ''}
                    onChange={(e) => handleAnswerChange(question.text, e.target.value)}
                    margin="normal"
                    multiline
                    rows={3}
                    required
                    disabled={submitting}
                  />
                ))}
              </>
            )}
            
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              fullWidth 
              sx={{ mt: 3 }}
              disabled={submitting}
              size="large"
            >
              {submitting ? <CircularProgress size={24} /> : "Подтвердить запись"}
            </Button>
          </form>
        </Paper>
      </Container>
    </EmptyLayout>
  );
};